import useSWR from 'swr';

import { useListing } from 'context/listingContext';
import { useProxyBids } from 'context/proxyBids';
import { clientApiGet } from 'lib/api_helper';
import { extendUrlByQueries } from 'lib/url_helpers';
import { Listing } from 'types';
import { ONE_HOUR } from 'utils/date';

const regularInterval = 10000;
const endingSoonInterval = 5000;

export const useRefreshListing = (id: number) => {
  const { isAuction, refreshListing, listing } = useListing();
  const { refetchProxyBids } = useProxyBids();

  useSWR<Listing>(
    isAuction
      ? extendUrlByQueries(`/api/listings/${id}`, { 'expand[]': ['model', 'issues.issue', 'accessories'] })
      : null,
    clientApiGet,
    {
      refreshInterval: data => {
        if (!data?.auctionInfo?.endDate) {
          return regularInterval;
        }
        const currentDate = new Date().getTime();
        const targetDate = new Date(data?.auctionInfo.endDate).getTime();

        if (targetDate - currentDate > ONE_HOUR) {
          return regularInterval;
        }

        return endingSoonInterval;
      },
      onSuccess: newListing => {
        if (newListing.auctionInfo.highestBid.id !== listing.auctionInfo.highestBid.id) {
          refreshListing(newListing);
          refetchProxyBids();
        }
      }
    }
  );
};

export const pollListing = (id: number, refreshListing: (listing: Listing) => void, listing: Listing) => {
  const refresh = async () =>
    await clientApiGet<Listing>(
      extendUrlByQueries(`/api/listings/${id}`, { 'expand[]': ['model', 'issues.issue', 'accessories'] })
    );

  const start = async () => {
    const intervalId = setInterval(async () => {
      const newListing = await refresh();

      if (listing && newListing && newListing?.auctionInfo?.highestBid?.id !== listing?.auctionInfo?.highestBid?.id) {
        refreshListing?.(newListing);
      }
    }, 1000);

    setTimeout(() => clearInterval(intervalId), 7000);
  };

  return start;
};
